/**
 * ThemeToggle - Premium theme switcher component
 * Allows users to toggle between light and dark modes
 */

import React from 'react';
import { useTheme } from '../contexts/ThemeContext';
import './ThemeToggle.css';

const ThemeToggle = ({ showLabel = true, size = 'medium' }) => {
  const { toggleTheme, isDark, isThemePinned, preference } = useTheme();

  // On pinned routes (auth / legal / help — see FORCED_LIGHT_PATHS in
  // ThemeContext) the page always renders light, but the switch stays
  // visible and live. It shows the user's PREFERENCE rather than what
  // is painted, so clicking it visibly moves and the choice takes
  // effect as soon as they leave the pinned page. Reading it off the
  // painted theme instead would leave it stuck on "Light" no matter
  // how often it was clicked.
  const showsDark = isThemePinned ? preference === 'dark' : isDark;
  const action = `Switch to ${showsDark ? 'light' : 'dark'} mode`;
  const description = isThemePinned
    ? `${action} — this page is always light, so it applies when you leave`
    : action;

  return (
    <button
      className={`theme-toggle-btn ${size}`}
      onClick={toggleTheme}
      aria-label={description}
      title={description}
    >
      <div className="theme-toggle-track">
        <div className="theme-toggle-icons">
          <span className="theme-icon sun">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="5" />
              <line x1="12" y1="1" x2="12" y2="3" />
              <line x1="12" y1="21" x2="12" y2="23" />
              <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
              <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
              <line x1="1" y1="12" x2="3" y2="12" />
              <line x1="21" y1="12" x2="23" y2="12" />
              <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
              <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
            </svg>
          </span>
          <span className="theme-icon moon">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
            </svg>
          </span>
        </div>
        <div className="theme-toggle-thumb">
          {showsDark ? (
            <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
              <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
            </svg>
          ) : (
            <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
              <circle cx="12" cy="12" r="5" />
            </svg>
          )}
        </div>
      </div>
      {showLabel && (
        <span className="theme-toggle-label">
          {showsDark ? 'Dark' : 'Light'}
        </span>
      )}
    </button>
  );
};

export default ThemeToggle;
